import ReadyForAction from "@mui/icons-material/AccessTime";
import NotReadyForAction from "@mui/icons-material/DoNotDisturbOnTotalSilence";
import Done from "@mui/icons-material/Done";
import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import {
  ReviewMode,
  SignatureType,
  SubmissionStatus,
  UserType,
} from "generated-graphql/graphql";
import {
  allNonSignersCompleted,
  getReviewerStatusLabel,
  hasCompletedReview,
  hasRejectedReview,
  type Reviewer,
} from "./submissionReviewerUtils";

interface SubmissionReviewerCellProps {
  reviewers: Reviewer[];
  reviewMode?: ReviewMode | null;
  submissionStatus?: SubmissionStatus | null;
  signatureType?: SignatureType | null;
  isStaff: boolean;
  type: "reviewer" | "signer";
}

function getReviewerName(reviewer: Reviewer): string {
  const person = reviewer.user.person;
  if (person?.first || person?.last) {
    return `${person.first || ""} ${person.last || ""}`.trim();
  }
  return reviewer.user.email;
}

/**
 * Determines the status icon for a reviewer based on review mode.
 *
 * - Parallel mode: All reviewers can act simultaneously (AccessTimeFilled if not completed)
 * - Sequential mode: Reviewers must go in order by reviewOrder
 *   - AccessTimeFilled if it's their turn (all preceding reviewers completed)
 *   - Group if waiting for preceding reviewers
 */
function getReviewerIcon(
  reviewer: Reviewer,
  allReviewers: Reviewer[],
  reviewMode?: ReviewMode | null
): React.ReactNode {
  const iconSx = { height: "16px", width: "16px", mr: 0.5 };

  if (hasRejectedReview(reviewer)) {
    return <NotReadyForAction sx={{ ...iconSx, color: "action.active" }} />;
  }

  if (hasCompletedReview(reviewer)) {
    return <Done sx={{ ...iconSx, color: "action.active" }} />;
  }

  if (reviewer.isSigner && !allNonSignersCompleted(allReviewers)) {
    return <NotReadyForAction sx={{ ...iconSx, color: "action.active" }} />;
  }

  if (reviewMode !== ReviewMode.Sequential) {
    return <ReadyForAction sx={{ ...iconSx, color: "action.active" }} />;
  }

  const reviewerOrder = reviewer.reviewOrder ?? 0;
  const precedingReviewers = allReviewers.filter(
    (r) => (r.reviewOrder ?? 0) < reviewerOrder
  );
  const allPrecedingComplete = precedingReviewers.every(hasCompletedReview);

  if (allPrecedingComplete) {
    return <ReadyForAction sx={{ ...iconSx, color: "action.active" }} />;
  }

  return <NotReadyForAction sx={{ ...iconSx, color: "action.active" }} />;
}

/**
 * A cell component for displaying reviewers with their status icons.
 * Replaces both the "Reviewer(s)" and "Waiting On" columns.
 */
export function SubmissionReviewerCell({
  reviewers,
  reviewMode,
  submissionStatus,
  isStaff,
  type,
}: SubmissionReviewerCellProps) {
  const theme = useTheme();

  const filteredByType = reviewers.filter((r) =>
    type === "reviewer" ? !r.isSigner : r.isSigner
  );

  const displayedReviewers = filteredByType
    .filter((r) => isStaff || r.user.type === UserType.Customer)
    .sort((a, b) => (a.reviewOrder ?? 0) - (b.reviewOrder ?? 0));

  if (displayedReviewers.length === 0) {
    return null;
  }

  return (
    <Stack
      spacing={0}
      sx={{
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        height: "100%",
        width: "100%",
        alignItems: "start",
        overflow: "hidden",
      }}
    >
      {displayedReviewers.map((reviewer) => {
        const name = getReviewerName(reviewer);
        const icon = getReviewerIcon(reviewer, reviewers, reviewMode);
        const status = getReviewerStatusLabel(
          reviewer,
          reviewers,
          reviewMode,
          submissionStatus
        );

        return (
          <Tooltip key={reviewer.id} title={status}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                overflow: "hidden",
              }}
            >
              {icon}
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {name}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
}
